import React, { createContext, useContext, useState, useEffect } from 'react';
import projectService from '../services/projectService';

const ProjectMenuContext = createContext();

export const useProjectMenu = () => {
  const context = useContext(ProjectMenuContext);
  if (!context) {
    throw new Error('useProjectMenu must be used within a ProjectMenuProvider');
  }
  return context;
};

export const ProjectMenuProvider = ({ children }) => {
  const [menuSettings, setMenuSettings] = useState({
    showEditOption: true,
    showCloneOption: true,
    showArchiveOption: true
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Fetch menu settings on mount
  useEffect(() => {
    const fetchMenuSettings = async () => {
      try {
        setLoading(true);
        const settings = await projectService.getMenuSettings();
        setMenuSettings(settings);
        setError(null);
      } catch (err) {
        console.error('Failed to fetch menu settings:', err);
        setError(err.message);
        // Keep default settings on error
      } finally {
        setLoading(false);
      }
    };

    fetchMenuSettings();
  }, []);

  // Update menu settings
  const updateMenuSettings = async (newSettings) => {
    try {
      const updatedSettings = await projectService.updateMenuSettings(newSettings);
      setMenuSettings(updatedSettings);
      setError(null);
      return updatedSettings;
    } catch (err) {
      console.error('Failed to update menu settings:', err);
      setError(err.message);
      throw err;
    }
  };

  // Toggle individual menu option
  const toggleMenuOption = async (option) => {
    const newSettings = {
      ...menuSettings,
      [option]: !menuSettings[option]
    };
    return await updateMenuSettings(newSettings);
  };

  const value = {
    menuSettings,
    loading,
    error,
    updateMenuSettings,
    toggleMenuOption
  };

  return (
    <ProjectMenuContext.Provider value={value}>
      {children}
    </ProjectMenuContext.Provider>
  );
};

export default ProjectMenuContext;